// App timezone helpers - the zone the Schedule page groups days, labels
// "Today"/"Tomorrow" and shows kickoff times in (default America/Toronto).
//
// Build-time override: REACT_APP_APP_TIMEZONE.  At startup initAppTimezone()
// also reads the backend's APP_TIMEZONE from /api/schedule/config so both
// sides agree.  Everything here is synchronous and falls back to the default.
//
// Day keys are plain "YYYY-MM-DD" strings; arithmetic on them (addDaysIso,
// weekStartIso) is timezone-free.

export const DEFAULT_APP_TIMEZONE = "America/Toronto";

let _tz = process.env.REACT_APP_APP_TIMEZONE || DEFAULT_APP_TIMEZONE;

const _valid = (tz) => {
  try { new Intl.DateTimeFormat("en-CA", { timeZone: tz }); return true; } catch { return false; }
};
if (!_valid(_tz)) _tz = DEFAULT_APP_TIMEZONE;

export const getAppTimezone = () => _tz;
export const setAppTimezone = (tz) => { if (tz && _valid(tz)) _tz = tz; };

export const initAppTimezone = async (backendUrl) => {
  try {
    const r = await fetch(`${backendUrl}/api/schedule/config`);
    if (r.ok) setAppTimezone((await r.json())?.timezone);
  } catch { /* keep the default */ }
};

/** Calendar date (YYYY-MM-DD) of an instant in the app timezone. */
export const appDateIso = (when = new Date()) => {
  const d = when instanceof Date ? when : new Date(when);
  if (isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: _tz, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};

export const appTodayIso = (now = new Date()) => appDateIso(now);

/** YYYY-MM-DD plus n days (calendar arithmetic, no timezone involved). */
export const addDaysIso = (iso, n) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export const appTomorrowIso = (now = new Date()) => addDaysIso(appTodayIso(now), 1);

/** Monday of the week containing iso (default: app-timezone today). */
export const weekStartIso = (iso) => {
  const base = iso || appTodayIso();
  const day = new Date(`${base}T12:00:00Z`).getUTCDay();   // 0=Sun
  return addDaysIso(base, -(day === 0 ? 6 : day - 1));
};

/** "yesterday" | "today" | "tomorrow" relative to the app-timezone date, else null. */
export const dayLabel = (iso, now = new Date()) => {
  const diff = Math.round(
    (new Date(`${iso}T12:00:00Z`) - new Date(`${appTodayIso(now)}T12:00:00Z`)) / 86400000);
  return diff === -1 ? "yesterday" : diff === 0 ? "today" : diff === 1 ? "tomorrow" : null;
};

/** Short weekday / "13 Oct" strings for a YYYY-MM-DD day key. */
export const fmtDayKey = (iso, opts, locale) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString(locale, { ...opts, timeZone: "UTC" });

/** Kickoff clock time (HH:MM) in the app timezone. */
export const fmtKickoffTime = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(locale, {
      hour: "2-digit", minute: "2-digit", timeZone: _tz,
    });
  } catch { return ""; }
};

/** Schedule day strip (Yesterday -> +7d) in the app timezone: "Today"/"Tom"
 *  follow that zone's calendar, not the browser's or UTC. */
export const dayStripDates = (now = new Date()) => {
  const base = appTodayIso(now);
  const out = [];
  for (let off = -1; off <= 7; off++) {
    const iso = addDaysIso(base, off);
    out.push({
      iso,
      label: off === -1 ? "Yest" : off === 0 ? "Today" : off === 1 ? "Tom"
        : fmtDayKey(iso, { weekday: "short" }, "en-US"),
      sub: fmtDayKey(iso, { day: "numeric", month: "short" }, "en-US"),
    });
  }
  return out;
};
